'use client'

import Image from 'next/image'
import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/services/it', label: 'IT Services' },
  { href: '/services/videography', label: 'Videography' },
  { href: '/services/graphic-design', label: 'Graphic Design' },
  { href: '/work', label: 'Work' },
  { href: '/blog', label: 'Insights' },
  { href: '/about', label: 'About' },
]

export default function Navbar() {
  const pathname = usePathname()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close menu on route change
  useEffect(() => {
    setMobileOpen(false)
  }, [pathname])

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  return (
    <>
      {/* ─── Main Nav Bar ─── */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
        className={`fixed top-0 left-0 right-0 z-[600] transition-all duration-500 ${
          scrolled || mobileOpen
            ? 'py-3 bg-[#0A101E]/95 backdrop-blur-xl border-b border-white/10'
            : 'py-5 bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between">
          {/* Logo — always on top */}
          <Link
            href="/"
            className="flex items-center relative z-[601]"
            onClick={() => setMobileOpen(false)}
          >
            <Image
              src="/logos/apex-visualtech/logo-transparent-svg.svg"
              alt="Apex Visualtech Logo"
              width={350}
              height={90}
              className="h-14 sm:h-16 w-auto object-contain"
              priority
            />
          </Link>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className={`nav-link text-sm tracking-[0.08em] font-body transition-colors duration-300 ${
                  pathname === link.href
                    ? 'active text-[#F8FAFC]'
                    : 'text-[#94A3B8] hover:text-[#F8FAFC]'
                }`}
                style={{ fontFamily: 'var(--font-dm-sans, "DM Sans", sans-serif)' }}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side: CTA & hamburger — always on top via z-index */}
          <div className="flex items-center gap-3 relative z-[601]">
            {/* Desktop CTA */}
            <Link
              href="/contact"
              className="hidden lg:flex items-center gap-2 px-5 py-2.5 bg-[#C8A96E] text-[#0F172A] text-sm font-semibold tracking-[0.08em] hover:bg-[#D4BA8A] transition-colors duration-300"
              style={{ fontFamily: 'var(--font-dm-sans, "DM Sans", sans-serif)' }}
            >
              Start a Project
            </Link>

            {/* Mobile: small "HIRE US" pill */}
            <Link
              href="/contact"
              className="lg:hidden text-[10px] tracking-[0.15em] text-[#C8A96E] border border-[#C8A96E]/40 px-3 py-1.5 hover:bg-[#C8A96E]/10 transition-colors"
              style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
              onClick={() => setMobileOpen(false)}
            >
              HIRE US
            </Link>

            {/* Hamburger / Close — animated swap */}
            <button
              onClick={() => setMobileOpen((prev) => !prev)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-[#F8FAFC] hover:text-[#C8A96E] transition-colors"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              <AnimatePresence mode="wait" initial={false}>
                {mobileOpen ? (
                  <motion.span
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="flex"
                  >
                    <X size={22} />
                  </motion.span>
                ) : (
                  <motion.span
                    key="open"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="flex"
                  >
                    <Menu size={22} />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          </div>
        </div>
      </motion.nav>

      {/* ─── Mobile Full-Screen Menu ─── */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: '-100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '-100%' }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[599] bg-[#0A101E] flex flex-col overflow-y-auto overscroll-contain"
          >
            {/* Content area — centred vertically */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 pt-24 pb-6">
              {/* Gold accent */}
              <div className="w-10 h-px bg-[#C8A96E] mb-8" />

              {/* Nav links */}
              <nav className="flex flex-col items-center w-full max-w-xs" aria-label="Mobile navigation">
                {navLinks.map((link, index) => {
                  const isActive = pathname === link.href
                  return (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, x: -24 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.08 + index * 0.05,
                        duration: 0.32,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="w-full"
                    >
                      <Link
                        href={link.href}
                        onClick={() => setMobileOpen(false)}
                        className={`group flex items-center justify-between w-full py-4 border-b transition-colors duration-200 ${
                          isActive
                            ? 'border-[#C8A96E]/50'
                            : 'border-white/[0.07] hover:border-[#C8A96E]/25'
                        }`}
                      >
                        <span
                          className={`text-[clamp(1.7rem,7vw,2.4rem)] leading-none tracking-[0.05em] transition-colors duration-200 ${
                            isActive
                              ? 'text-[#C8A96E]'
                              : 'text-[#E2E8F0] group-hover:text-[#C8A96E]'
                          }`}
                          style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)' }}
                        >
                          {link.label}
                        </span>
                        <span
                          className={`text-[10px] tracking-[0.15em] transition-colors duration-200 ${
                            isActive ? 'text-[#C8A96E]' : 'text-[#475569] group-hover:text-[#C8A96E]/60'
                          }`}
                          style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
                        >
                          {isActive ? '●' : '→'}
                        </span>
                      </Link>
                    </motion.div>
                  )
                })}
              </nav>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 + navLinks.length * 0.05 + 0.06, duration: 0.35 }}
                className="mt-8 w-full max-w-xs"
              >
                <Link
                  href="/contact"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center justify-center w-full py-4 bg-[#C8A96E] text-[#0F172A] tracking-[0.2em] font-bold hover:bg-[#D4BA8A] active:scale-95 transition-all duration-300"
                  style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', fontSize: '1.15rem' }}
                >
                  START A PROJECT
                </Link>
              </motion.div>
            </div>

            {/* Bottom Social Row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.48, duration: 0.35 }}
              className="flex justify-center gap-8 py-6 border-t border-white/[0.07]"
            >
              {[
                { label: 'LINKEDIN', href: 'https://www.linkedin.com/company/apex-visualtech/' },
                { label: 'INSTAGRAM', href: 'https://www.instagram.com/apexvisualtech/' },
                { label: 'FACEBOOK', href: 'https://www.facebook.com/people/Apex-VisualTech/61575538461471/' },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#64748B] hover:text-[#C8A96E] transition-colors text-[10px] tracking-[0.2em]"
                  style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
                >
                  {social.label}
                </a>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

